import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconArrowDownRight, IconArrowUpRight } from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { UnifiedDiff } from '@/components/diff/UnifiedDiff'

import {
  applyCandidatePrompt,
  fetchChangedCalls,
  revertAppliedPrompt,
} from '@/reducers/promptimizer/promptimizer.actions'
import { STOPPED_EARLY, selectRun } from '@/reducers/promptimizer/promptimizer.redux'
import { ChangedCallsList } from './ChangedCallsList'

// Stable empty map, so a store with no promptimizer slice doesn't hand `useSelector` a new object each render.
const NO_CALLS = {}

const FIXED = 'fixed'
const BROKE = 'broke'

const VIEW = { DIFF: 'diff', FULL: 'full' }

/**
 * How many of the measured calls a prompt answered the same way as the reference.
 *
 * Reported as a count over the sample rather than a percentage, deliberately. The reference is a stronger model
 * running this same prompt, so "45 of 50 agree" is a claim we can stand behind and "90% accurate" is not.
 */
const agreement = (metrics) => {
  const scored = metrics?.label_match

  if (isNil(scored?.n_correct) || isNil(scored?.n_scored)) {
    return null
  }

  return { correct: scored.n_correct, total: scored.n_scored }
}

const Score = ({ label, value, highlight }) => (
  <div className={classNames('copilot-optimize__score', { 'is-better': highlight })}>
    <p className="copilot-optimize__score-label">{label}</p>
    <p className="copilot-optimize__score-value">
      {isNil(value) ? '—' : value.correct}
      {!isNil(value) && <span className="copilot-optimize__score-total">/{value.total} agree</span>}
    </p>
  </div>
)

/**
 * Said when the scorecard's eligibility settings have moved since the run.
 *
 * A note, not a warning: the result is still correct and still worth adopting, it is just measured over the calls
 * the criterion covered at the time. Styling it as an error would tell people to distrust a number that is fine.
 */
const EligibilityMoved = ({ moved }) =>
  moved ? (
    <p className="copilot-optimize__panel-note">
      Your scorecard eligibility settings have changed since this ran, so these numbers are over the
      calls this criterion covered at the time.
    </p>
  ) : null

/**
 * Which model the run measured on — whatever the editor's dropdown held at click time, which need not be the model
 * the criterion runs today.
 *
 * Rendered on every outcome: a result survives a reload and is visible to everyone on the criterion, so a reader
 * who was not there has no other way to know the verdict describes a model nobody selected.
 */
const MeasuredOn = ({ targetModel, targetEffort, targetModelId, currentModelId }) => {
  if (isNil(targetModel)) {
    return null
  }

  return (
    <p className="copilot-optimize__panel-note">
      Measured on <strong>{targetModel}</strong>
      {!isEmpty(targetEffort) && ` at ${targetEffort} reasoning`}.
      {!isNil(currentModelId) && targetModelId !== currentModelId && (
        <>
          {' '}
          <strong>This criterion is set to a different model</strong>, so these numbers do not
          describe what it does today.
        </>
      )}
    </p>
  )
}

/**
 * What the run produced, once it is done.
 *
 * Three outcomes, none of them a failure to hide: a version beat the current prompt, nothing did, or nothing
 * usable came back. "Nothing beat it" is a real answer — a prompt the model already follows has little room to
 * gain — so it is stated plainly with the numbers still shown.
 *
 * ⚠ ONLY THE WINNER IS SHOWN. The spread across candidates is a statistical artefact — the winner is the best of N
 * draws scored on the very calls it is reported on — and reading it that way is our job, not the user's. Nothing
 * is lost: every candidate and its metrics stay in the poll response and in `promptimizer.candidates`. The one
 * cost is that a non-winning version cannot be applied.
 */
export const OptimizeResult = ({ criteriaScid, currentModelId, setFieldValue }) => {
  const dispatch = useDispatch()
  const {
    optimizationId,
    candidates,
    winner,
    incumbentMetrics,
    originalPrompt,
    pendingAdoption,
    targetModel,
    targetEffort,
    targetModelId,
    stoppedEarly,
    eligibilityChanged,
  } = useSelector((state) => selectRun(state, criteriaScid))
  // Keyed by candidate rather than by criterion — candidate ids are unique, and the same list is what the drill-in
  // renders.
  const changedCalls = useSelector((state) => state.promptimizer?.changedCalls ?? NO_CALLS)
  const [openCandidateId, setOpenCandidateId] = useState(null)
  // A rewritten prompt runs to thousands of characters, almost all of it the prompt you already wrote, so the
  // diff is the default. The whole text is one click away for reading before applying it.
  const [view, setView] = useState(VIEW.DIFF)

  // Fetched as soon as the result lands rather than when someone opens the list, because the fixed/broke counts
  // are the headline of the panel and they aggregate these same rows.
  const winnerId = winner?.candidate_id
  const calls = isNil(winnerId) ? undefined : changedCalls[winnerId]

  useEffect(() => {
    if (!isNil(winnerId) && isNil(calls)) {
      dispatch(fetchChangedCalls({ optimizationId, candidateId: winnerId, criteriaScid }))
    }
  }, [dispatch, optimizationId, winnerId, calls, criteriaScid])

  // Both of these are runs with no candidates, and they mean opposite things — one is a fault, the other is the
  // best outcome available. `stoppedEarly` is what tells them apart; the empty list alone cannot.
  if (stoppedEarly === STOPPED_EARLY.AT_CEILING) {
    const matched = agreement(incumbentMetrics)

    return (
      <div className="copilot-optimize__panel">
        <p className="copilot-optimize__panel-title">Nothing to improve here</p>
        <p className="copilot-optimize__panel-note">
          Your prompt already matches the reference on
          {isNil(matched) ? ' every call we measured' : ` all ${matched.total} calls we measured`},
          so there was no room for a rewrite to do better. We stopped before writing any.
        </p>
        {/* Needed MORE here than on the ordinary result: "nothing to improve" reads as a verdict on the
            criterion, when it may be a verdict on a model nobody selected. */}
        <MeasuredOn
          targetModel={targetModel}
          targetEffort={targetEffort}
          targetModelId={targetModelId}
          currentModelId={currentModelId}
        />
        <EligibilityMoved moved={eligibilityChanged} />
      </div>
    )
  }

  if (isEmpty(candidates)) {
    return (
      <div className="copilot-optimize__panel">
        <p className="copilot-optimize__panel-title">No usable versions came back</p>
        <p className="copilot-optimize__panel-note">
          The run finished but every version it wrote failed to produce a valid answer. Your prompt
          is unchanged.
        </p>
        {/* Named here too: "every version failed" invites blaming the prompt, when it may be a model nobody
            selected that could not answer. */}
        <MeasuredOn
          targetModel={targetModel}
          targetEffort={targetEffort}
          targetModelId={targetModelId}
          currentModelId={currentModelId}
        />
      </div>
    )
  }

  const incumbent = agreement(incumbentMetrics)
  const best = agreement(winner?.metrics)
  const improved = !isNil(incumbent) && !isNil(best) && best.correct > incumbent.correct
  const changed = calls ?? []
  const nFixed = changed.filter((call) => call.change === FIXED).length
  const nBroke = changed.filter((call) => call.change === BROKE).length
  // Whether this run's suggestion is currently sitting in the form. `pendingAdoption` is set by the apply and
  // cleared by the save or the revert, so it is already the answer — no second flag to keep in step.
  const applied = pendingAdoption?.optimizationId === optimizationId

  return (
    <div className="copilot-optimize__panel">
      <p className="copilot-optimize__panel-title">
        {improved
          ? 'Found a better version'
          : 'Your prompt is already the best of the ones we tried'}
      </p>
      <p className="copilot-optimize__panel-note">
        {improved
          ? `Measured on ${best.total} calls.`
          : // Leads with the number: on a run against a cheaper model that number IS the result, and nothing
            // needing a rewrite is a good outcome rather than a null one.
            `Your prompt agreed with the reference on ${incumbent?.correct} of ${incumbent?.total} calls. None of the ${candidates.length} alternatives we wrote did better.`}
      </p>

      <MeasuredOn
        targetModel={targetModel}
        targetEffort={targetEffort}
        targetModelId={targetModelId}
        currentModelId={currentModelId}
      />
      <EligibilityMoved moved={eligibilityChanged} />

      <div className="copilot-optimize__scores">
        <Score label="Your prompt now" value={incumbent} />
        <Score
          label={improved ? 'New version' : 'Best alternative'}
          value={best}
          highlight={improved}
        />
      </div>

      {!isNil(calls) && (
        <div className="copilot-optimize__diff-summary">
          <span>
            <IconArrowUpRight className="copilot-optimize__diff-icon is-better" />
            Fixes {nFixed} calls
          </span>
          <span>
            <IconArrowDownRight className="copilot-optimize__diff-icon is-worse" />
            Breaks {nBroke} calls
          </span>
        </div>
      )}

      {improved && (
        <>
          <div className="copilot-optimize__view-toggle">
            <Button text active={view === VIEW.DIFF} onClick={() => setView(VIEW.DIFF)}>
              What changed
            </Button>
            <Button text active={view === VIEW.FULL} onClick={() => setView(VIEW.FULL)}>
              Full prompt
            </Button>
          </div>

          {view === VIEW.DIFF ? (
            <UnifiedDiff
              oldText={originalPrompt}
              newText={winner.prompt_text}
              emptyLabel="This version is word for word what you already have."
            />
          ) : (
            <pre className="copilot-optimize__prompt">{winner.prompt_text}</pre>
          )}
        </>
      )}

      {/* The calls are worth reading on BOTH outcomes — a version that fixed one call and broke two is exactly
          when someone wants to look. Applying stays gated on `improved`: there is nothing to adopt when the
          prompt you already have did best. */}
      {(improved || !isEmpty(changed)) && (
        <div className="copilot-optimize__actions">
          {improved &&
            (applied ? (
              // Only reachable once the text is in the form, because before that there is nothing to put back. It
              // reverts `user_prompt` and nothing else — the scorecard's own Discard is the blunt instrument that
              // throws away every other edit made this session.
              <Button
                disabled={isEmpty(originalPrompt)}
                disabledTooltip="We don't have your original prompt for this run, so there is nothing to put back."
                onClick={() =>
                  dispatch(revertAppliedPrompt({ originalPrompt, criteriaScid, setFieldValue }))
                }
              >
                Put my prompt back
              </Button>
            ) : (
              <Button
                brand
                onClick={() =>
                  dispatch(
                    applyCandidatePrompt({
                      optimizationId,
                      candidate: winner,
                      criteriaScid,
                      setFieldValue,
                    })
                  )
                }
              >
                Use this prompt
              </Button>
            ))}
          {!isEmpty(changed) && (
            <Button
              onClick={() =>
                setOpenCandidateId(
                  openCandidateId === winner.candidate_id ? null : winner.candidate_id
                )
              }
            >
              {openCandidateId === winner.candidate_id
                ? 'Hide the calls'
                : 'See the calls it changed'}
            </Button>
          )}
        </div>
      )}

      {openCandidateId === winner?.candidate_id && (
        <ChangedCallsList
          optimizationId={optimizationId}
          candidateId={winner.candidate_id}
          criteriaScid={criteriaScid}
        />
      )}
    </div>
  )
}
